// ================== Generic CRUD Helpers ==================
import { apiClient } from "api/APIClient";
import { Pathway_Backend, PathwayFlow } from "api/GenericPathwayInterfaces";

/**
 * Generates standard CRUD functions for a given entity.
 * Each entity keeps its own function names for clarity.
 */
export function entityAPI<T>(module: string, basePath: string) {
  const base = `${module}/${basePath}`;

  function payloadHasFile(payload: any) {
    if (!payload || typeof payload !== "object") return false;
    return Object.values(payload).some((v) => v instanceof File);
  }

  function toFormData(payload: any): FormData {
    const fd = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fd.append(key, value instanceof Blob ? value : String(value));
      }
    });
    return fd;
  }

  return {
    getAll: async (params?: Record<string, any>): Promise<T[]> => {
      const { data } = await apiClient.get<T[]>(`${base}/all/`, { params });
      return data;
    },

    getById: async (id: number, params?: Record<string, any>): Promise<T> => {
      const { data } = await apiClient.get<T>(`${base}/${id}/`, { params });
      return data;
    },

    getByToken: async (token: string, params?: Record<string, any>): Promise<T> => {
      const { data } = await apiClient.get<T>(`${base}/${token}/`, { params });
      return data;
    },

    getByLoggedInUser: async (params?: Record<string, any>): Promise<T> => {
      const { data } = await apiClient.get<T>(`${base}/`, { params });
      return data;
    },

    create: async (
      payload: Partial<T>,
      params?: Record<string, any>
    ): Promise<T> => {
      const isFileUpload = payloadHasFile(payload);
      const body = isFileUpload ? toFormData(payload) : payload;

      const { data } = await apiClient.post<T>(`${base}/all/`, body, {
        params,
        headers: isFileUpload ? { "Content-Type": "multipart/form-data" } : {},
      });

      return data;
    },

    update: async (
      id: number,
      payload: Partial<T>,
      params?: Record<string, any>
    ): Promise<T> => {
      const isFileUpload = payloadHasFile(payload);
      const body = isFileUpload ? toFormData(payload) : payload;

      const { data } = await apiClient.put<T>(`${base}/${id}/`, body, {
        params,
        headers: isFileUpload ? { "Content-Type": "multipart/form-data" } : {},
      });

      return data;
    },

    updateField: async (
      id: number,
      field: string,
      value: any,
      params?: Record<string, any>
    ): Promise<T> => {
      const isFile = value instanceof File;

      if (isFile) {
        const fd = new FormData();
        fd.append(field, value);

        const { data } = await apiClient.patch<T>(`${base}/${id}/`, fd, {
          params,
          headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
      }

      const { data } = await apiClient.patch<T>(
        `${base}/${id}/`,
        { [field]: value },
        { params }
      );
      return data;
    },

    delete: async (id: number, params?: Record<string, any>): Promise<void> => {
      await apiClient.delete(`${base}/${id}/`, { params });
    },
  };
}


/**
 * Generates standard Log CRUD functions for a given parent entity.
 */
export function logAPI<T>(module: string, basePath: string) {
  const base = `${module}/${basePath}`;

  return {
    getAll: async (): Promise<T[]> => {
      const { data } = await apiClient.get<T[]>(`${base}/all/`);
      return data;
    },

    getAllByParentId: async (parentId: number): Promise<T[]> => {
      const { data } = await apiClient.get<T[]>(`${base}/${parentId}/logs/all/`);
      return data;
    },

    getAllByParentToken: async (token: string): Promise<T[]> => {
      const { data } = await apiClient.get<T[]>(`${base}/${token}/logs/all/`);
      return data;
    },

    getAllByParent: async (
      parentId?: number,
      token?: string
    ): Promise<T[]> => {
      if (token) {
        const { data } = await apiClient.get<T[]>(`${base}/${token}/logs/all/`);
        return data;
      }

      if (parentId == null) {
        throw new Error("Either parentId or token is required");
      }

      const { data } = await apiClient.get<T[]>(`${base}/${parentId}/logs/all/`);
      return data;
    },

    getById: async (logId: number): Promise<T> => {
      const { data } = await apiClient.get<T>(`${base}/logs/${logId}/`);
      return data;
    },

    create: async (parentId: number, payload: Partial<T>): Promise<T> => {
      const { data } = await apiClient.post<T>(
        `${base}/${parentId}/logs/all/`,
        payload
      );
      return data;
    },

    createWithParentToken: async (
      token: string,
      payload: Partial<T>
    ): Promise<T> => {
      const { data } = await apiClient.post<T>(
        `${base}/${token}/logs/all/`,
        payload
      );
      return data;
    },

    createForParent: async (
      payload: Partial<T>,
      parentId?: number,
      token?: string
    ): Promise<T> => {
      if (token) {
        const { data } = await apiClient.post<T>(
          `${base}/${token}/logs/all/`,
          payload
        );
        return data;
      }

      if (parentId == null) {
        throw new Error("Either parentId or token is required");
      }

      const { data } = await apiClient.post<T>(
        `${base}/${parentId}/logs/all/`,
        payload
      );
      return data;
    },

    update: async (logId: number, payload: Partial<T>): Promise<T> => {
      const { data } = await apiClient.put<T>(
        `${base}/logs/${logId}/`,
        payload
      );
      return data;
    },

    updateField: async (
      logId: number,
      field: keyof T,
      value: any
    ): Promise<T> => {
      const { data } = await apiClient.patch<T>(
        `${base}/logs/${logId}/`,
        { [field]: value }
      );
      return data;
    },

    delete: async (logId: number): Promise<void> => {
      await apiClient.delete(`${base}/logs/${logId}/`);
    },
  };
}



/**
 * Generic data fetcher for any Pathway type.
 * 
 * @param url - API endpoint to fetch
 * @param transformFn - transformation function converting backend → frontend Flow
 */


export async function getPathwayData<
  TBackend extends Pathway_Backend,
  TFlow extends PathwayFlow
>(
  url: string,
  transformFn: (backendData: TBackend) => TFlow
): Promise<TFlow> {
  const { data } = await apiClient.get<TBackend>(url);

  return transformFn(data);
}